import * as THREE from "three";
import type { RacePresentationSnapshot } from "./types";

export type SuntraceMotionSnapshot = RacePresentationSnapshot & {
  acceleration?: number;
};

export type SuntraceMotionLoadProfile = {
  speedLoad: number;
  lateralLoad: number;
  cornerLoad: number;
  verticalLoad: number;
  boostLoad: number;
  sprayLoad: number;
  motionScale: number;
};

export type SuntraceSpringState = {
  value: number;
  velocity: number;
};

const finiteOr = (value: number | undefined, fallback = 0): number =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

const finiteClamp = (value: number | undefined, minimum: number, maximum: number): number =>
  THREE.MathUtils.clamp(finiteOr(value), minimum, maximum);

const MAX_SPRING_VALUE = 1_000_000;
const MAX_SPRING_VELOCITY = 1_000_000;
const MAX_SPRING_FREQUENCY = 480;
const MAX_DAMPING_RATIO = 1.2;
const MAX_SPRING_SUBSTEPS = 128;

export const calculateSuntraceMotionLoad = (
  snapshot: SuntraceMotionSnapshot,
  reducedMotion: boolean,
  target: SuntraceMotionLoadProfile = {
    speedLoad: 0,
    lateralLoad: 0,
    cornerLoad: 0,
    verticalLoad: 0,
    boostLoad: 0,
    sprayLoad: 0,
    motionScale: 1,
  },
): SuntraceMotionLoadProfile => {
  const speed = Math.max(0, finiteOr(snapshot.speed));
  const steering = finiteClamp(snapshot.steering, -1, 1);
  const heading = finiteClamp(snapshot.heading, -1.5, 1.5);
  const slip = finiteClamp(snapshot.slip, -1.5, 1.5);
  const drift = finiteClamp(snapshot.drift, 0, 1);
  const acceleration = finiteOr(snapshot.acceleration);
  const impact = finiteClamp(snapshot.impact, 0, 1);
  const speedLoad = THREE.MathUtils.smoothstep(speed, 14, 78);
  const lateralLoad = THREE.MathUtils.clamp(
    (steering * 0.52 + heading * 1.7 + slip * 0.5) * speedLoad,
    -1,
    1,
  );
  const cornerLoad = THREE.MathUtils.clamp(
    (
      Math.abs(steering) * 0.48 +
      Math.abs(heading) * 1.65 +
      Math.abs(slip) * 0.66
    ) * THREE.MathUtils.smoothstep(speed, 18, 60),
    0,
    1,
  );
  const accelerationLoad = THREE.MathUtils.clamp(acceleration / 12, -1, 1);

  target.speedLoad = speedLoad;
  target.lateralLoad = lateralLoad;
  target.cornerLoad = cornerLoad;
  target.verticalLoad = THREE.MathUtils.clamp(
    accelerationLoad * 0.65 + impact * 0.7,
    -1,
    1,
  );
  target.boostLoad = finiteClamp(snapshot.boost, 0, 1);
  target.sprayLoad = THREE.MathUtils.clamp(
    (Math.abs(slip) * 0.72 + drift * 0.6) * speedLoad,
    0,
    1,
  );
  target.motionScale = reducedMotion ? 0.2 : 1;
  return target;
};

export const stepSuntraceSpring = (
  state: SuntraceSpringState,
  target: number,
  stiffness: number,
  dampingRatio: number,
  deltaSeconds: number,
): void => {
  const delta = THREE.MathUtils.clamp(finiteOr(deltaSeconds), 0, 0.05);
  const naturalFrequency = Math.min(
    Math.sqrt(Math.max(0, finiteOr(stiffness))),
    MAX_SPRING_FREQUENCY,
  );
  const springStiffness = naturalFrequency * naturalFrequency;
  const damping = 2 * finiteClamp(dampingRatio, 0, MAX_DAMPING_RATIO) * naturalFrequency;
  const substeps = Math.min(
    MAX_SPRING_SUBSTEPS,
    Math.max(1, Math.ceil(delta * Math.max(naturalFrequency * 2, damping))),
  );
  const substepDelta = delta / substeps;
  const springTarget = finiteClamp(target, -MAX_SPRING_VALUE, MAX_SPRING_VALUE);
  let value = finiteClamp(state.value, -MAX_SPRING_VALUE, MAX_SPRING_VALUE);
  let velocity = finiteClamp(state.velocity, -MAX_SPRING_VELOCITY, MAX_SPRING_VELOCITY);

  for (let step = 0; step < substeps; step += 1) {
    const acceleration = springStiffness * (springTarget - value) - damping * velocity;
    velocity = finiteClamp(
      velocity + acceleration * substepDelta,
      -MAX_SPRING_VELOCITY,
      MAX_SPRING_VELOCITY,
    );
    value = finiteClamp(
      value + velocity * substepDelta,
      -MAX_SPRING_VALUE,
      MAX_SPRING_VALUE,
    );
  }

  state.value = value;
  state.velocity = velocity;
};
